import { useRef, useState, type JSX } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import type { AppDispatch, RootState } from "@/store/store";
import { useViewLanding } from "@/hooks/useViewLanding/useViewLanding";
import ModalWithoutHeaderAndFooterAtCenter from "@/components/common/ModalWithoutHeaderAndFooterAtCenter/ModalWithoutHeaderAndFooterAtCenter";
import {
  selectViewDetail,
  selectIsProvenanceDismissed,
  buildProvenanceDismissKey,
  provenanceDismissed,
  persistProvenanceDismissed,
} from "@/store/slices/viewsV2/viewsV2Slice";
import {
  unavailableItems,
  groupUnavailableByKind,
} from "@utils/viewDefinition/enrichment";
import type { UnresolvableReason } from "@utils/viewDefinition/types";
import UnavailableLedger from "./UnavailableLedger";

/**
 * Reasons describing a reference that is gone rather than withheld. Customer Success
 * cannot restore these, so the "extend your contract" line is wrong advice for them.
 * Everything else -- including FILTERS_NOT_ACCESSIBLE, MEMBERS_NOT_ACCESSIBLE and
 * NOT_APPLICABLE_IN_YOUR_MARKETS -- is entitlement shaped and does warrant the line.
 */
const UNRECOVERABLE_REASONS: ReadonlySet<UnresolvableReason> = new Set([
  "NOT_FOUND",
  "INVALID_ID",
  "DEACTIVATED",
  "SUPERSEDED",
]);

const ViewIcon = (): JSX.Element => (
  <svg width="13" height="13" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <g stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4.5 3.5h11a1 1 0 0 1 1 1v12l-6.5-3.4L3.5 16.5v-12a1 1 0 0 1 1-1Z" />
    </g>
  </svg>
);

const CloseIcon = (): JSX.Element => (
  <svg width="15" height="15" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path
      d="M5.5 5.5l9 9M14.5 5.5l-9 9"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SHELL = "w-[560px] max-w-[92vw] flex flex-col max-h-[88vh]";
const HEAD = "relative shrink-0 px-[26px] pt-5 pb-4 border-b border-neutral-200 bg-neutral-50";
const EYEBROW = "flex items-center gap-[7px] mb-[9px] text-text-50";
const EYEBROW_TEXT = "text-[11px] font-semibold uppercase tracking-[0.07em]";
const TITLE = "text-[17px] font-bold text-text-950 tracking-[-0.022em] leading-[1.25] pr-[34px]";
const SUBLINE = "text-[13px] text-text-50 mt-[5px] leading-[1.5] max-w-[430px]";
const CLOSE_BTN =
  "absolute top-4 right-4 w-7 h-7 rounded-[7px] flex items-center justify-center text-text-50 transition hover:bg-neutral-100 hover:text-text-950 cursor-pointer outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500";
const FOOTER =
  "px-[26px] py-3 border-t border-neutral-200 bg-neutral-50 flex items-center justify-end gap-[9px]";
const DISMISS_BTN =
  "rounded-[9px] cursor-pointer border border-neutral-200 bg-white px-[15px] py-[9px] text-[13px] tracking-[-0.005em] font-semibold text-text-300 transition hover:bg-neutral-25 hover:border-neutral-300";

/**
 * Provenance affordance for a view opened via `?viewId=`.
 *
 * - Fires `useViewLanding` (fetches + caches the server view detail).
 * - Unresolvable references and not yet dismissed → a modal naming each item and why it
 *   is out of reach, in the same treatment as the shared-link adoption modal.
 * - Landing status "gone" (404/422 on fetch) → an acknowledgeable modal.
 *   The screen still renders from the URL's own params either way.
 *
 * Migration status is internal: it stays on the cached detail for debugging and Sentry
 * context, and is never surfaced to the user.
 */
export const ViewProvenanceNotice = (): JSX.Element | null => {
  const { status } = useViewLanding();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const viewId = new URLSearchParams(location.search).get("viewId");
  const [goneAcknowledged, setGoneAcknowledged] = useState(false);
  const acknowledgeRef = useRef<HTMLButtonElement>(null);

  const userId = useSelector((state: RootState) => state.userDetail?.id);
  const isDataFetched = useSelector(
    (state: RootState) => state.activeGroup.bootstrapDataStatus.isDataFetched,
  );
  const detail = useSelector((state: RootState) =>
    viewId ? selectViewDetail(state, viewId) : undefined,
  );
  const limitedAccessItems = detail ? unavailableItems(detail) : [];
  const dismissKey = viewId ? buildProvenanceDismissKey(userId, viewId, limitedAccessItems) : "";
  const isDismissed = useSelector((state: RootState) =>
    viewId ? selectIsProvenanceDismissed(state, dismissKey) : false,
  );
  const dismissals = useSelector((state: RootState) => state.viewsV2.provenanceDismissed);

  if (!viewId) return null;

  if (status === "gone") {
    if (goneAcknowledged) return null;
    const acknowledge = (): void => setGoneAcknowledged(true);
    return (
      <ModalWithoutHeaderAndFooterAtCenter
        open
        onClose={acknowledge}
        closeOnOverlayClick={false}
        initialFocusRef={acknowledgeRef}
        className="modal-elevated"
      >
        <div className={SHELL}>
          <div className={HEAD}>
            <div className={EYEBROW}>
              <ViewIcon />
              <span className={EYEBROW_TEXT}>Saved view</span>
            </div>
            <h2 className={TITLE}>This saved view no longer exists</h2>
            <p className={SUBLINE}>The screen below reflects the link&rsquo;s settings.</p>
            <button type="button" aria-label="Dismiss" onClick={acknowledge} className={CLOSE_BTN}>
              <CloseIcon />
            </button>
          </div>
          <div className={FOOTER}>
            <button
              ref={acknowledgeRef}
              type="button"
              onClick={acknowledge}
              className={DISMISS_BTN}
            >
              Understood
            </button>
          </div>
        </div>
      </ModalWithoutHeaderAndFooterAtCenter>
    );
  }

  // Absence of `references` isn't an all-clear (best-effort enrichment) — this
  // only ever renders on a non-empty list, never an affirmative "all available".
  if (isDismissed || limitedAccessItems.length === 0 || !isDataFetched) {
    return null;
  }

  const groups = groupUnavailableByKind(limitedAccessItems);
  const isRecoverable = limitedAccessItems.some((item) => !UNRECOVERABLE_REASONS.has(item.reason));

  const handleDismiss = (): void => {
    const at = Date.now();
    dispatch(provenanceDismissed({ key: dismissKey, at }));
    persistProvenanceDismissed({ ...dismissals, [dismissKey]: at });
  };

  return (
    <ModalWithoutHeaderAndFooterAtCenter
      open
      onClose={handleDismiss}
      closeOnOverlayClick={false}
      initialFocusRef={acknowledgeRef}
      className="modal-elevated"
    >
      <div className={SHELL}>
        <div className={HEAD}>
          <div className={EYEBROW}>
            <ViewIcon />
            <span className={EYEBROW_TEXT}>Saved view</span>
          </div>
          <h2 className={TITLE}>Some items in this view aren&rsquo;t available to you</h2>
          <p className={SUBLINE}>The view still opens. Everything else has been applied.</p>
          <button type="button" aria-label="Dismiss" onClick={handleDismiss} className={CLOSE_BTN}>
            <CloseIcon />
          </button>
        </div>

        <div className="px-[26px] pt-1 pb-[18px] flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
          <UnavailableLedger groups={groups} />
        </div>

        {isRecoverable ? (
          <div className="px-[26px] py-3 border-t border-neutral-200 bg-neutral-25">
            <p className="text-[12px] text-text-300 leading-[1.55]">
              To add these to your plan, contact your customer success representative about
              extending your contract.
            </p>
          </div>
        ) : null}

        <div className={FOOTER}>
          <button ref={acknowledgeRef} type="button" onClick={handleDismiss} className={DISMISS_BTN}>
            Understood
          </button>
        </div>
      </div>
    </ModalWithoutHeaderAndFooterAtCenter>
  );
};

export default ViewProvenanceNotice;
